import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  Search,
  BarChart3,
  Brain,
  CheckCircle,
  Clock,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Zap,
  Target,
  Users,
  FileText,
  ChevronRight,
  ChevronLeft,
  Info,
  Star,
  Layers,
  TrendingUp,
  Settings,
  Award,
  Rocket,
  Database,
  Lightbulb,
  Trophy,
  Eye,
  GitBranch,
  Building,
  Home
} from 'lucide-react';

import ProblemDefinitionPhase from '../workflow/ProblemDefinitionPhase';
import DeepResearchPhase from '../workflow/DeepResearchPhase';
import TrizIntegrationPhase from '../workflow/TrizIntegrationPhase';
import IdeaEvaluationPhase from '../workflow/IdeaEvaluationPhase';
import DeliveryPhase from '../workflow/DeliveryPhase';
import innovationIdeasDataRaw from '../../data/innovationIdeas.json';
import type { InnovationIdeasData } from '../../types/innovationIdeas';

const innovationIdeasData = innovationIdeasDataRaw as InnovationIdeasData;

interface WorkflowPhase {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  status: 'locked' | 'available' | 'in-progress' | 'completed';
  subSteps: string[];
  outputs: string[];
}

interface SkyflowDummyData {
  companyName: string;
  industry: string;
  currentChallenges: string[];
  competitors: string[];
  techStack: string[];
  problemStatements: string[];
  trizPrinciples: any[];
  generatedIdeas: any[];
  evaluationScores: any[];
  selectedSolutions: any[];
  architecturalViews: any[];
}

interface PhaseResults {
  [key: string]: any;
}

const TrizInnovationWorkflow: React.FC = () => {
  const [currentPhase, setCurrentPhase] = useState<number>(0);
  const [activePhaseComponent, setActivePhaseComponent] = useState<string | null>(null);
  const [phaseResults, setPhaseResults] = useState<PhaseResults>({});
  const [skyflowData, setSkyflowData] = useState<SkyflowDummyData | null>(null);
  const [completedPhases, setCompletedPhases] = useState<Set<string>>(new Set());
  const [phaseInternalStates, setPhaseInternalStates] = useState<{[key: string]: any}>({});
  const [demoMode, setDemoMode] = useState(false);

  const phases: WorkflowPhase[] = [
    {
      id: 'problem-definition',
      name: 'Problem & Context Analysis',
      description: 'Define world view, context, constraints, and objectives',
      icon: Target,
      status: 'available',
      subSteps: [
        'World View Research',
        'Company Context Analysis',
        'Problem View Formulation',
        'Constraints Circle Definition',
        'Definition of Good Circle'
      ],
      outputs: ['Problem Statement', 'Context Map', 'Constraints Document']
    },
    {
      id: 'deep-research',
      name: 'Deep Research & Knowledge',
      description: 'AI-powered research with technical paper analysis',
      icon: Search,
      status: 'available',
      subSteps: [
        'Google Deep Research',
        'Technical Paper Analysis',
        'PhD-level Knowledge Extraction',
        'Bibliography Tree Index',
        'Structured JSON Summaries'
      ],
      outputs: ['Research Report', 'Technical Bibliography', 'Knowledge Base']
    },
    {
      id: 'triz-integration',
      name: 'TRIZ Integration',
      description: 'Apply TRIZ principles for innovative idea generation',
      icon: Lightbulb,
      status: 'available',
      subSteps: [
        'TRIZ Principles Application',
        'Contradiction Analysis',
        'Mechanized Thinking Process',
        'Seed Ideas Generation',
        'LLM Enhancement'
      ],
      outputs: ['TRIZ Analysis', 'Innovation Matrix', `Idea Catalog (${innovationIdeasData.metadata.totalIdeas}+ ideas)`]
    },
    {
      id: 'idea-evaluation',
      name: 'Idea Evaluation & Ranking',
      description: 'Tournament-style competition for idea ranking',
      icon: Trophy,
      status: 'available',
      subSteps: [
        'Game Rules Creation',
        'Tournament Competition',
        'Multi-parameter Scoring',
        'Rating Matrix Generation',
        'Top 10 Selection'
      ],
      outputs: ['Ranking Matrix', 'Evaluation Scores', 'Selected Ideas']
    },
    {
      id: 'delivery',
      name: 'Output Delivery',
      description: 'Final roadmap and comprehensive documentation',
      icon: Award,
      status: 'available',
      subSteps: [
        'Roadmap Document Creation',
        'Visual Presentation',
        'Quarter-on-Quarter Planning',
        'Justification Package',
        'Actionable Insights'
      ],
      outputs: ['Product Roadmap', 'Executive Summary', 'Implementation Guide']
    }
  ];

  const skyflowDummyData: SkyflowDummyData = {
    companyName: 'Skyflow Inc.',
    industry: 'Data Privacy & Security',
    currentChallenges: [
      'Complex regulatory compliance (GDPR, CCPA, HIPAA)',
      'Scaling PII detection across diverse data formats',
      'Real-time processing requirements',
      'Integration with existing enterprise systems',
      'Cost optimization for high-volume processing'
    ],
    competitors: ['Privacera', 'Immuta', 'Varonis', 'BigID', 'OneTrust'],
    techStack: ['React', 'Node.js', 'PostgreSQL', 'MongoDB', 'Kubernetes', 'AWS', 'Apache Kafka'],
    problemStatements: [
      'How to automatically detect and classify PII in real-time across multiple data formats?',
      'How to ensure 99.9% accuracy in PII detection while maintaining sub-second response times?',
      'How to handle diverse regulatory requirements across different jurisdictions?'
    ],
    trizPrinciples: [
      { id: 1, name: 'Segmentation', application: 'Separate PII detection by data type and sensitivity level' },
      { id: 15, name: 'Dynamics', application: 'Adaptive classification based on context and usage patterns' },
      { id: 35, name: 'Parameter Changes', application: 'Transform data representation for better detection accuracy' }
    ],
    generatedIdeas: innovationIdeasData.ideas.slice(0, 50).map(idea => ({
      id: idea.id,
      title: idea.title,
      score: idea.score,
      category: idea.category,
      description: idea.description,
      difficulty: idea.difficulty,
      timeToImplement: idea.timeToImplement,
      impact: idea.impact,
      tags: idea.tags
    })),
    evaluationScores: [
      { criterion: 'Technical Feasibility', score: 8.5 },
      { criterion: 'Market Impact', score: 9.2 },
      { criterion: 'Competitive Advantage', score: 8.8 }
    ],
    selectedSolutions: [
      'AI-powered PII detection with 99.9% accuracy',
      'Real-time processing pipeline with sub-second latency',
      'Unified compliance dashboard for multi-jurisdiction requirements'
    ],
    architecturalViews: [
      { type: 'Business Flow', components: ['Data Ingestion', 'PII Detection', 'Compliance Reporting'] },
      { type: 'Technical Architecture', components: ['ML Pipeline', 'Data Lake', 'API Gateway'] }
    ]
  };

  useEffect(() => {
    // Read URL parameters for company data
    const urlParams = new URLSearchParams(window.location.search);
    const company = urlParams.get('company');
    const industry = urlParams.get('industry');
    const objective = urlParams.get('objective');
    const challenges = urlParams.get('challenges');
    
    // Initialize with data from URL if available, otherwise use Skyflow dummy data
    const contextualData = {
      ...skyflowDummyData,
      companyName: company || skyflowDummyData.companyName,
      industry: industry || skyflowDummyData.industry,
      // Add user's objective and challenges to the problems list if provided
      problemStatements: objective && challenges ? [
        `How to achieve: ${objective}`,
        `How to overcome: ${challenges}`,
        ...skyflowDummyData.problemStatements
      ] : skyflowDummyData.problemStatements
    };
    
    setSkyflowData(contextualData);

    // Automatically open Problem Definition phase if coming from RoadmapInnovation
    if (company && objective && challenges) {
      setActivePhaseComponent('problem-definition');
    }
  }, []);

  const startPhase = (phaseId: string) => {
    setActivePhaseComponent(phaseId);
  };

  const navigateToPhase = (direction: 'next' | 'previous') => {
    if (!activePhaseComponent) return;
    
    const currentIndex = phases.findIndex(p => p.id === activePhaseComponent);
    let newIndex;
    
    if (direction === 'next' && currentIndex < phases.length - 1) {
      newIndex = currentIndex + 1;
    } else if (direction === 'previous' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else {
      return;
    }
    
    setActivePhaseComponent(phases[newIndex].id);
  };

  const getCurrentPhaseInfo = () => {
    if (!activePhaseComponent) return null;
    const currentIndex = phases.findIndex(p => p.id === activePhaseComponent);
    const currentPhaseData = phases[currentIndex];
    return {
      current: currentPhaseData,
      index: currentIndex,
      total: phases.length,
      hasNext: currentIndex < phases.length - 1,
      hasPrevious: currentIndex > 0,
      nextPhase: currentIndex < phases.length - 1 ? phases[currentIndex + 1] : null,
      previousPhase: currentIndex > 0 ? phases[currentIndex - 1] : null
    };
  };

  const handlePhaseComplete = (phaseId: string, results: any) => {
    setPhaseResults(prev => ({ ...prev, [phaseId]: results }));
    setCompletedPhases(prev => new Set([...prev, phaseId]));
    
    // Update current phase tracker (all phases remain available)
    const currentIndex = phases.findIndex(p => p.id === phaseId);
    setCurrentPhase(prev => Math.max(prev, currentIndex + 1));
    
    // Auto-navigate to next phase if available, otherwise return to overview
    if (currentIndex < phases.length - 1) {
      const nextPhase = phases[currentIndex + 1];
      setActivePhaseComponent(nextPhase.id);
    } else {
      setActivePhaseComponent(null);
    }
  };

  const savePhaseInternalState = (phaseId: string, state: any) => {
    setPhaseInternalStates(prev => ({ ...prev, [phaseId]: state }));
  };

  const runQuickDemo = async () => {
    setDemoMode(true);
    
    // Simulate completing all phases quickly with dummy results
    const demoResults = {
      'problem-definition': { problemStatement: 'Automated PII detection and compliance', completed: true },
      'deep-research': { researchSummary: 'Latest privacy technologies analyzed', completed: true },
      'triz-integration': { 
        generatedIdeas: innovationIdeasData.ideas.slice(0, 100),
        totalIdeas: innovationIdeasData.metadata.totalIdeas,
        completed: true 
      },
      'idea-evaluation': { selectedIdeas: skyflowDummyData.selectedSolutions, completed: true },
      'delivery': { roadmap: 'Innovation roadmap generated', completed: true }
    };
    
    // Complete all phases with a small delay between each
    for (let i = 0; i < phases.length; i++) {
      setTimeout(() => {
        const phaseId = phases[i].id;
        // @ts-ignore
        setPhaseResults(prev => ({ ...prev, [phaseId]: demoResults[phaseId] }));
        setCompletedPhases(prev => new Set([...prev, phaseId]));
        setCurrentPhase(i + 1);
        
        if (i === phases.length - 1) {
          // All phases completed
          setTimeout(() => {
            setDemoMode(false);
            setActivePhaseComponent(null);
          }, 500);
        }
      }, i * 800); // 800ms delay between each phase completion
    }
  };

  const renderPhaseComponent = () => {
    if (!activePhaseComponent) return null;

    switch (activePhaseComponent) {
      case 'problem-definition':
        return (
          <ProblemDefinitionPhase
            companyData={skyflowData}
            onComplete={(results) => handlePhaseComplete('problem-definition', results)}
            initialState={phaseInternalStates['problem-definition']}
            onStateChange={(state) => savePhaseInternalState('problem-definition', state)}
          />
        );
      
      case 'deep-research':
        return (
          <DeepResearchPhase
            companyName={skyflowData?.companyName || 'Skyflow Inc.'}
            domain={skyflowData?.industry || 'Data Privacy & Security'}
            onComplete={(results) => handlePhaseComplete('deep-research', results)}
            initialState={phaseInternalStates['deep-research']}
            onStateChange={(state) => savePhaseInternalState('deep-research', state)}
          />
        );
      
      case 'triz-integration':
        return (
          <TrizIntegrationPhase
            companyData={skyflowData}
            researchResults={phaseResults['deep-research']}
            onComplete={(results) => handlePhaseComplete('triz-integration', results)}
          />
        );
      
      case 'idea-evaluation':
        return (
          <IdeaEvaluationPhase
            ideas={phaseResults['triz-integration']?.generatedIdeas || []}
            onComplete={(results) => handlePhaseComplete('idea-evaluation', results)}
          />
        );
      
      case 'delivery':
        return (
          <DeliveryPhase
            onComplete={(results) => handlePhaseComplete('delivery', results)}
          />
        );
      
      default:
        return (
          <div className="sqsft-card backdrop-blur-xl rounded-2xl p-8 text-center shadow-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
            <h3 className="text-xl sqsft-heading-2 mb-4">Premium Phase Experience</h3>
            <p className="sqsft-body mb-6" style={{ color: 'rgb(var(--color-text-secondary))' }}>
              This advanced phase component provides immediate insights and interactive analysis.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActivePhaseComponent(null)}
              className="px-6 py-3 sqsft-btn-primary text-white font-semibold rounded-xl"
            >
              Back to Overview
            </motion.button>
          </div>
        );
    }
  };

  const getPhaseColor = (status: string) => {
    switch (status) {
      case 'completed': return 'from-emerald-400 to-emerald-600';
      case 'in-progress': return 'from-blue-400 to-blue-600';
      case 'available': return 'from-blue-400 to-indigo-600';
      default: return 'from-slate-400 to-slate-600';
    }
  };



  return (
    <div className="min-h-screen relative overflow-auto" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(66,102,83,0.1),transparent)] opacity-30"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(13,86,48,0.1),transparent)] opacity-30"></div>
      </div>

      <div className="relative z-20 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="sqsft-card rounded-lg p-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
              <div className="flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">1. Roadmap Input</span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <div className="flex items-center space-x-2" style={{ color: 'rgb(var(--color-deep-green))' }}>
                  <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgb(var(--color-deep-green))' }}>
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  </div>
                  <span className="font-semibold">2. Innovation Workflow</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <Lightbulb className="w-6 h-6" style={{ color: 'rgb(var(--color-deep-green))' }} />
              <h1 className="text-2xl sqsft-heading-1">
                TRIZ Innovation Workflow
              </h1>
            </div>
            <p className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
              5-Phase Iterative Product Roadmap Innovation System
            </p>
          </div>

          {/* Skyflow Data Section */}
          {skyflowData && (
            <div className="sqsft-card rounded-xl p-6 mb-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(209, 213, 219, 0.5)' }}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="mb-4 lg:mb-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 rounded-lg sqsft-gradient-primary flex items-center justify-center text-white">
                      <Building className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg sqsft-heading-3">{skyflowData.companyName}</h2>
                      <p className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        Current Innovation Project
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm w-full lg:w-auto">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
                    <p className="sqsft-caption mb-1">Industry</p>
                    <div className="flex items-center space-x-1.5">
                      <Layers className="w-3.5 h-3.5" style={{ color: 'rgb(var(--color-text-secondary))' }} />
                      <p className="sqsft-body font-medium">{skyflowData.industry}</p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
                    <p className="sqsft-caption mb-1">Focus Area</p>
                    <div className="flex items-center space-x-1.5">
                      <Target className="w-3.5 h-3.5" style={{ color: 'rgb(var(--color-text-secondary))' }} />
                      <p className="sqsft-body font-medium">PII Detection & Compliance</p>
                    </div>
                  </div>
                  {completedPhases.has('triz-integration') && (
                    <div className="p-3 rounded-lg col-span-2 sm:col-span-1" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
                       <p className="sqsft-caption mb-1">Ideas Generated</p>
                       <div className="flex items-center space-x-1.5">
                         <Lightbulb className="w-3.5 h-3.5" style={{ color: 'rgb(var(--color-text-secondary))' }} />
                         <p className="sqsft-body font-medium">{innovationIdeasData.metadata.totalIdeas}+</p>
                       </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Idea Categories Overview */}
              <div className="mt-5 pt-4 border-t" style={{ borderColor: 'rgba(209, 213, 219, 0.7)'}}>
                <p className="sqsft-caption mb-3">Innovation Categories Available:</p>
                <div className="flex flex-wrap gap-2">
                  {innovationIdeasData.metadata.categories.slice(0, 7).map((category, index) => (
                    <div 
                      key={index}
                      className="flex items-center space-x-2 px-3 py-1.5 text-xs rounded-full font-medium"
                      style={{ 
                        backgroundColor: 'rgba(66, 102, 83, 0.1)', 
                        color: 'rgb(var(--color-deep-green))' 
                      }}
                    >
                      <Zap className="w-3 h-3" />
                      <span>{category}</span>
                    </div>
                  ))}
                  {innovationIdeasData.metadata.categories.length > 7 && (
                    <div className="px-3 py-1.5 text-xs rounded-full font-medium" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', color: 'rgb(var(--color-text-secondary))' }}>
                      +{innovationIdeasData.metadata.categories.length - 7} more
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Progress Section */}
          {!activePhaseComponent && (
            <div className="mt-8 p-6 sqsft-gradient-subtle rounded-xl" style={{ border: '1px solid rgba(66, 102, 83, 0.2)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg sqsft-heading-3 mb-1">Workflow Progress</h3>
                  <p className="text-sm sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>Complete all phases to generate your innovation roadmap</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold" style={{ color: 'rgb(var(--color-deep-green))' }}>
                    {Math.round((completedPhases.size / phases.length) * 100)}%
                  </div>
                  <div className="text-sm sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                    {completedPhases.size} of {phases.length} completed
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-2 bg-white rounded-full overflow-hidden">
                    <div 
                      className="h-full sqsft-gradient-primary rounded-full transition-all duration-500"
                      style={{ width: `${(completedPhases.size / phases.length) * 100}%` }}
                    />
                  </div>
                </div>
                
                {completedPhases.size === phases.length && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center space-x-2 text-sm font-medium" style={{ color: 'rgb(var(--color-royal-gold))' }}
                  >
                    <Award className="w-4 h-4" />
                    <span>All phases completed! Your innovation roadmap is ready.</span>
                  </motion.div>
                )}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="mt-8">
            {activePhaseComponent ? (
              <div>
                {/* Enhanced Navigation Header */}
                <div className="mb-6 space-y-4">
                  {/* Breadcrumb Navigation */}
                  <div className="flex items-center space-x-2 text-sm sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                    <Home className="w-4 h-4" />
                    <span>TRIZ Workflow</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="sqsft-heading-4">
                      {getCurrentPhaseInfo()?.current.name}
                    </span>
                    {getCurrentPhaseInfo() && (
                      <span className="sqsft-caption">
                        ({getCurrentPhaseInfo()!.index + 1} of {getCurrentPhaseInfo()!.total})
                      </span>
                    )}
                  </div>
                  
                  {/* Navigation Controls - Mobile Responsive */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActivePhaseComponent(null)}
                        className="flex items-center space-x-2 px-3 py-2 sqsft-btn-ghost rounded-lg transition-all duration-300 shadow-sm text-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid rgba(209, 213, 219, 0.5)' }}
                      >
                        <Home className="w-4 h-4" />
                        <span className="hidden sm:block">Overview</span>
                      </motion.button>
                      
                      {getCurrentPhaseInfo()?.hasPrevious && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => navigateToPhase('previous')}
                          className="flex items-center space-x-2 px-3 py-2 sqsft-btn-ghost rounded-lg transition-all duration-300 shadow-sm text-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid rgba(209, 213, 219, 0.5)' }}
                        >
                          <ArrowLeft className="w-4 h-4" />
                          <span className="hidden sm:block">Previous</span>
                        </motion.button>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      
                      {!getCurrentPhaseInfo()?.hasNext && completedPhases.size === phases.length && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setActivePhaseComponent(null)}
                          className="flex items-center space-x-2 px-4 py-2 sqsft-gradient-gold text-white rounded-lg hover:shadow-lg transition-all duration-300 text-sm"
                        >
                          <Award className="w-4 h-4" />
                          <span>Complete</span>
                        </motion.button>
                      )}
                    </div>
                  </div>
                  
                  {/* Phase Progress Bar - Mobile Responsive */}
                  <div className="sqsft-card backdrop-blur-xl rounded-lg p-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-1 sm:space-y-0">
                      <span className="text-sm sqsft-heading-4">Workflow Progress</span>
                      <span className="text-sm sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        {completedPhases.size} of {phases.length} completed ({Math.round((completedPhases.size / phases.length) * 100)}%)
                      </span>
                    </div>
                    
                    {/* Desktop: Individual phase indicators */}
                    <div className="hidden md:flex items-center space-x-2">
                      {phases.map((phase, index) => {
                        const isCompleted = completedPhases.has(phase.id);
                        const isCurrent = phase.id === activePhaseComponent;
                        return (
                          <div
                            key={phase.id}
                            className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                              isCompleted
                                ? 'sqsft-gradient-gold'
                                : isCurrent
                                ? 'sqsft-gradient-primary'
                                : 'bg-gray-200'
                            }`}
                            title={phase.name}
                          />
                        );
                      })}
                    </div>
                    
                    {/* Mobile: Single progress bar */}
                    <div className="md:hidden">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full sqsft-gradient-primary rounded-full transition-all duration-500"
                          style={{ width: `${(completedPhases.size / phases.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {renderPhaseComponent()}
              </div>
                          ) : (
              <div className="px-4 py-8">
                {/* Quick Navigation Summary */}
                {completedPhases.size > 0 && (
                  <div className="mb-8 sqsft-card backdrop-blur-xl rounded-xl p-6 shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg sqsft-heading-3 mb-2">Quick Resume</h3>
                        <p className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                          Continue where you left off or jump to any completed phase
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {Array.from(completedPhases).slice(0, 3).map((phaseId) => {
                          const phase = phases.find(p => p.id === phaseId);
                          return phase ? (
                            <motion.button
                              key={phaseId}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => startPhase(phaseId)}
                              className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 text-sm" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: 'rgb(var(--color-royal-gold))' }}
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>{phase.name}</span>
                            </motion.button>
                          ) : null;
                        })}
                        {completedPhases.size > 3 && (
                          <span className="sqsft-small" style={{ color: 'rgb(var(--color-text-muted))' }}>+{completedPhases.size - 3} more</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {/* Header Section */}
                <div className="mb-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                    <div>
                      <h2 className="text-2xl sqsft-heading-2 mb-2">
                        Innovation Workflow
                      </h2>
                      <p className="sqsft-small" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        Complete each phase to build your innovation roadmap
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={runQuickDemo}
                        disabled={demoMode}
                        className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium"
                        style={{ 
                          backgroundColor: demoMode ? 'rgba(212, 175, 55, 0.3)' : 'rgba(212, 175, 55, 0.1)', 
                          color: 'rgb(var(--color-royal-gold))',
                          border: '1px solid rgba(212, 175, 55, 0.3)'
                        }}
                      >
                        <Zap className="w-4 h-4" />
                        <span>{demoMode ? 'Demo Running...' : 'Quick Demo'}</span>
                      </motion.button>
                      {/* Removed autoStartEnabled display */}
                    </div>
                  </div>
                </div>
                
                {/* Flow Chart Container */}
                <div className="relative max-w-7xl mx-auto">
                  {/* Phases Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    {phases.map((phase, index) => {
                      const isCompleted = completedPhases.has(phase.id);
                      const phaseStatus = isCompleted ? 'completed' : phase.status;
                      
                      return (
                        <motion.div
                          key={phase.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          {/* Phase Card */}
                          <motion.div
                            whileHover={{ y: -4 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative cursor-pointer"
                            onClick={() => startPhase(phase.id)}
                          >
                            <div className="h-full p-5 rounded-lg sqsft-card transition-all duration-200" style={{ border: '1px solid rgb(var(--color-border))' }}>
                              {/* Header with Number */}
                              <div className="flex items-start space-x-3 mb-4">
                                <div className="w-8 h-8 rounded-lg sqsft-gradient-primary flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                                  {index + 1}
                                </div>
                                <div className={`p-2.5 rounded-lg`} style={{ backgroundColor: isCompleted ? 'rgba(212, 175, 55, 0.1)' : 'rgba(66, 102, 83, 0.1)' }}>
                                  <phase.icon className={`w-6 h-6`} style={{ color: isCompleted ? 'rgb(var(--color-royal-gold))' : 'rgb(var(--color-deep-green))' }} />
                                </div>
                              </div>
                              
                              {/* Content */}
                              <div className="space-y-2">
                                <h3 className="text-base sqsft-heading-4">
                                  {phase.name}
                                </h3>
                                <p className="text-sm sqsft-body line-clamp-2 leading-relaxed" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                                  {phase.description}
                                </p>
                                
                                {/* Footer */}
                                <div className="pt-3 flex items-center justify-end">
                                  
                                  {/* Status/Action */}
                                  <div className={`text-sm font-medium`} style={{ color: isCompleted ? 'rgb(var(--color-royal-gold))' : 'rgb(var(--color-deep-green))' }}>
                                    {isCompleted ? (
                                      <div className="flex items-center space-x-1">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        <span>Done</span>
                                      </div>
                                    ) : (
                                      <span>Start →</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrizInnovationWorkflow;